import i18n from '@/i18n';
import { numberLocale } from '@/lib/locale';

export type PromotionType = 'VIP_STORES' | 'PROMOTED_CATEGORIES' | 'POPULAR_STORES' | 'BIG_CASHBACK';
export type CampaignStatus = 'DRAFT' | 'SCHEDULED' | 'ACTIVE' | 'PAUSED' | 'COMPLETED' | 'CANCELLED';
export type ApplicationStatus = 'DRAFT' | 'SUBMITTED' | 'UNDER_REVIEW' | 'APPROVED' | 'REJECTED' | 'WITHDRAWN';
export type CashbackStatus = 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'ACTIVE' | 'EXPIRED' | 'CANCELLED';

export const PROMOTION_TYPES: PromotionType[] = ['VIP_STORES', 'PROMOTED_CATEGORIES', 'POPULAR_STORES', 'BIG_CASHBACK'];

export const promotionLabel: Record<PromotionType, string> = {
  get VIP_STORES() { return i18n.t('misc_marketing.promo_VIP_STORES'); },
  get PROMOTED_CATEGORIES() { return i18n.t('misc_marketing.promo_PROMOTED_CATEGORIES'); },
  get POPULAR_STORES() { return i18n.t('misc_marketing.promo_POPULAR_STORES'); },
  get BIG_CASHBACK() { return i18n.t('misc_marketing.promo_BIG_CASHBACK'); },
};

export const promotionDescription: Record<PromotionType, string> = {
  get VIP_STORES() { return i18n.t('misc_marketing.promo_desc_VIP_STORES'); },
  get PROMOTED_CATEGORIES() { return i18n.t('misc_marketing.promo_desc_PROMOTED_CATEGORIES'); },
  get POPULAR_STORES() { return i18n.t('misc_marketing.promo_desc_POPULAR_STORES'); },
  get BIG_CASHBACK() { return i18n.t('misc_marketing.promo_desc_BIG_CASHBACK'); },
};

export const campaignStatusLabel: Record<CampaignStatus, string> = {
  get DRAFT() { return i18n.t('misc_marketing.campaign_DRAFT'); },
  get SCHEDULED() { return i18n.t('misc_marketing.campaign_SCHEDULED'); },
  get ACTIVE() { return i18n.t('misc_marketing.campaign_ACTIVE'); },
  get PAUSED() { return i18n.t('misc_marketing.campaign_PAUSED'); },
  get COMPLETED() { return i18n.t('misc_marketing.campaign_COMPLETED'); },
  get CANCELLED() { return i18n.t('misc_marketing.campaign_CANCELLED'); },
};

export const applicationStatusLabel: Record<ApplicationStatus, string> = {
  get DRAFT() { return i18n.t('misc_marketing.application_DRAFT'); },
  get SUBMITTED() { return i18n.t('misc_marketing.application_SUBMITTED'); },
  get UNDER_REVIEW() { return i18n.t('misc_marketing.application_UNDER_REVIEW'); },
  get APPROVED() { return i18n.t('misc_marketing.application_APPROVED'); },
  get REJECTED() { return i18n.t('misc_marketing.application_REJECTED'); },
  get WITHDRAWN() { return i18n.t('misc_marketing.application_WITHDRAWN'); },
};

export const cashbackStatusLabel: Record<CashbackStatus, string> = {
  get DRAFT() { return i18n.t('misc_marketing.cashback_DRAFT'); },
  get SUBMITTED() { return i18n.t('misc_marketing.cashback_SUBMITTED'); },
  get APPROVED() { return i18n.t('misc_marketing.cashback_APPROVED'); },
  get REJECTED() { return i18n.t('misc_marketing.cashback_REJECTED'); },
  get ACTIVE() { return i18n.t('misc_marketing.cashback_ACTIVE'); },
  get EXPIRED() { return i18n.t('misc_marketing.cashback_EXPIRED'); },
  get CANCELLED() { return i18n.t('misc_marketing.cashback_CANCELLED'); },
};

type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline';

export const campaignStatusVariant: Record<CampaignStatus, BadgeVariant> = {
  DRAFT: 'outline',
  SCHEDULED: 'secondary',
  ACTIVE: 'default',
  PAUSED: 'secondary',
  COMPLETED: 'outline',
  CANCELLED: 'destructive',
};

export const applicationStatusVariant: Record<ApplicationStatus, BadgeVariant> = {
  DRAFT: 'outline',
  SUBMITTED: 'secondary',
  UNDER_REVIEW: 'secondary',
  APPROVED: 'default',
  REJECTED: 'destructive',
  WITHDRAWN: 'outline',
};

export const cashbackStatusVariant: Record<CashbackStatus, BadgeVariant> = {
  DRAFT: 'outline',
  SUBMITTED: 'secondary',
  APPROVED: 'default',
  REJECTED: 'destructive',
  ACTIVE: 'default',
  EXPIRED: 'outline',
  CANCELLED: 'destructive',
};

export const CAMPAIGN_TRANSITIONS: Record<CampaignStatus, CampaignStatus[]> = {
  DRAFT: ['SCHEDULED', 'CANCELLED'],
  SCHEDULED: ['ACTIVE', 'CANCELLED', 'DRAFT'],
  ACTIVE: ['PAUSED', 'COMPLETED', 'CANCELLED'],
  PAUSED: ['ACTIVE', 'COMPLETED', 'CANCELLED'],
  COMPLETED: [],
  CANCELLED: [],
};

export const percentToBps = (percent: number | string): number =>
  Math.round(Number(String(percent).replace(',', '.')) * 100);

export const bpsToPercent = (bps: number | null | undefined): string => {
  if (bps === null || bps === undefined) return '—';
  const p = bps / 100;
  return `${Number.isInteger(p) ? p : p.toFixed(2)}%`;
};

export const formatPeriod = (start?: string | null, end?: string | null): string => {
  const f = (d?: string | null) => (d ? new Date(d).toLocaleDateString(numberLocale()) : '—');
  return `${f(start)} — ${f(end)}`;
};

export const isLive = (status: string, start?: string | null, end?: string | null): boolean => {
  if (status !== 'ACTIVE' || !start || !end) return false;
  const now = Date.now();
  return new Date(start).getTime() <= now && new Date(end).getTime() > now;
};

export const daysLeft = (end?: string | null): number | null => {
  if (!end) return null;
  return Math.ceil((new Date(end).getTime() - Date.now()) / 86400000);
};
